import React, { createContext, useContext } from "react";
import type {
  AnswerListItem,
  BoundEvent,
  BoundFormField,
  BoundFormStatus,
  BoundActionItems,
  BoundBudget,
  BoundCalendarEvents,
  BoundChatTranscript,
  BoundCheckIns,
  BoundFinanceList,
  BoundJobNotes,
  BoundJobTags,
  BoundJobTasks,
  BoundJobAttachments,
  BoundJobField,
  BoundLogo,
  BoundWorkLog,
  BoundScore,
  BoundSignoff,
  FormOutputContext,
  ScoreBreakdownSnapshot,
} from "@eaco/forms-core";

/**
 * Mock bindings provided to output-only renderers (Score / FormResults
 * / EventTimeline) when no real `data.bound*` payload is present.
 *
 * Used by the FormOutputEditor's Design canvas so designers see
 * representative data while they're still arranging the document.
 * The Preview tab bakes the same bindings into element.data via
 * `applyOutputMockBindings`, so renderers there hit the data path
 * directly and never need this context.
 *
 * Null = not in mock mode → renderers fall back to their empty state.
 */
export interface MockBindings {
  /**
   * All payloads are optional: a form-output canvas provides the form
   * group, a report canvas provides the report group — renderers
   * already null-guard every access.
   */
  answers?: AnswerListItem[];
  score?: BoundScore;
  events?: BoundEvent[];
  formName?: string;
  /** Per-field bound payloads keyed by FORM_FIELDS id. */
  formFields?: Record<string, BoundFormField>;
  /**
   * Raw ISO values for the date-valued form fields, so a `form-field`
   * element on the canvas can re-format to its own chosen format the
   * way the server's binder does against a real result.
   */
  formFieldInstants?: Record<string, string | null>;
  /** Form status pill payload — design canvas falls back to this. */
  formStatus?: BoundFormStatus;
  /** Sign-off details payload — design canvas falls back to this. */
  formSignoff?: BoundSignoff;
  /**
   * Sample job + property a form output's `details-table` / `job-field`
   * bind against on the design canvas (forms-core mockFormOutputContext).
   */
  outputContext?: FormOutputContext | null;
  /** Sample per-response counts for the `score-breakdown` canvas. */
  scoreBreakdown?: ScoreBreakdownSnapshot | null;
  /**
   * Entity-report payloads (job_report_document editor) — report
   * canvases populate them from forms-core mockJobReportData() via
   * renderEntityReport().
   */
  jobAttachments?: BoundJobAttachments;
  logo?: BoundLogo;
  /** Per-field bound payloads keyed by JOB_FIELDS id. */
  jobFields?: Record<string, BoundJobField>;
  workLog?: BoundWorkLog;
  checkIns?: BoundCheckIns;
  calendarEvents?: BoundCalendarEvents;
  /** Finance lists keyed by element family (see JobFinanceList). */
  finance?: {
    invoices?: BoundFinanceList;
    bills?: BoundFinanceList;
    quotesSubmitted?: BoundFinanceList;
    quotesReceived?: BoundFinanceList;
    purchaseOrders?: BoundFinanceList;
    expenses?: BoundFinanceList;
  };
  budget?: BoundBudget | null;
  jobNotes?: BoundJobNotes;
  actionItems?: BoundActionItems;
  jobTasks?: BoundJobTasks;
  jobTags?: BoundJobTags;
  chatTranscript?: BoundChatTranscript;
  /** For a job-chat-transcript element with source EXTERNAL. */
  externalChatTranscript?: BoundChatTranscript;
}

const MockBindingsContext = createContext<MockBindings | null>(null);

export const MockBindingsProvider: React.FC<{
  value: MockBindings | null;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <MockBindingsContext.Provider value={value}>
    {children}
  </MockBindingsContext.Provider>
);

export const useMockBindings = (): MockBindings | null =>
  useContext(MockBindingsContext);
